/**
 * SBAvv — Auftragsverarbeitungsvertrag (Art. 28 DSGVO) zwischen Kanzlei
 * (Verantwortliche) und ivoliq (Auftragsverarbeiter) für das SB-Portal.
 * Route: /steuerberater-avv (öffentlich). Version 1.0, Juni 2026.
 * Hinweis: Die Zustimmung erfolgt gemeinsam mit dem SB-Vertrag (/steuerberater-agb).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ShieldCheck, FileText, Database, Users, Server, Lock,
  AlertCircle, Trash2, ClipboardCheck, Scale,
} from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

const SectionCard = ({ icon: Icon, title, children, testId }) => (
  <Card data-testid={testId}>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <Icon className="h-5 w-5 text-emerald-600 shrink-0" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3 text-sm text-gray-700">{children}</CardContent>
  </Card>
);

export default function SBAvv() {
  useSEO({
    title: 'SB-Auftragsverarbeitungsvertrag (SB-AVV) | ivoliq',
    description: 'Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO zwischen Steuerberatungskanzleien und ivoliq für die Nutzung des SB-Portals.',
    canonical: 'https://ivoliq.io/steuerberater-avv',
    robots: 'index,follow',
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="sb-avv-page">
      <div className="mb-8">
        <Link to="/" className="text-emerald-600 hover:text-emerald-700 mb-4 inline-block">
          ← Zurück
        </Link>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          SB-Auftragsverarbeitungsvertrag (SB-AVV)
        </h1>
        <p className="text-gray-600">
          Vereinbarung gemäß Art. 28 Abs. 3 DSGVO zwischen Kanzlei (Verantwortliche)
          und ivoliq (Auftragsverarbeiter)
        </p>
        <p className="text-sm text-gray-500 mt-2">Version 1.0 · Stand: Juni 2026</p>
      </div>

      <div className="space-y-6">
        <SectionCard icon={ShieldCheck} title="Präambel & Parteien" testId="sb-avv-preamble">
          <p>
            Dieser Vertrag regelt die Verarbeitung personenbezogener Daten, die ivoliq im
            Auftrag der Kanzlei im Rahmen des ivoliq SB-Portals durchführt. Er ergänzt den{' '}
            <Link to="/steuerberater-agb" className="text-emerald-600 hover:underline">ivoliq SB-Vertrag</Link>{' '}
            und wird gemeinsam mit diesem geschlossen (protokollierte Zustimmung im SB-Portal).
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Auftragsverarbeiter:</strong> ivoliq GmbH, FN 680579 b (Handelsgericht Wien),
              Raffelspergergasse 11/2/2, 1190 Wien, Österreich („ivoliq")
            </li>
            <li>
              <strong>Verantwortliche:</strong> Die im SB-Portal registrierte Steuerberatungs-
              bzw. Buchhaltungskanzlei („Kanzlei")
            </li>
          </ul>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
            <p className="text-gray-800">
              <strong>Rollenklärung:</strong> Die Kanzlei ist für die im Rahmen ihres Mandats
              verarbeiteten Mandantendaten datenschutzrechtlich eigenständige Verantwortliche.
              Soweit ihre Mandanten zugleich eigene ivoliq-Nutzer sind, gilt für deren
              Verhältnis zu ivoliq zusätzlich der allgemeine{' '}
              <Link to="/avv" className="text-emerald-600 hover:underline">Nutzer-AVV</Link>.
            </p>
          </div>
        </SectionCard>

        <SectionCard icon={FileText} title="§ 1 Gegenstand & Dauer" testId="sb-avv-subject">
          <p>
            Gegenstand ist die Bereitstellung des ivoliq SB-Portals (Mandantenverwaltung,
            Beleg- und Buchungsprüfung, Periodenabschlüsse, Umsatzsteuer-Voranmeldungen,
            Exporte, Kommunikation mit Mandanten). Die Laufzeit entspricht der Laufzeit des
            SB-Vertrags; mit dessen Beendigung endet auch dieser AVV.
          </p>
        </SectionCard>

        <SectionCard icon={Database} title="§ 2 Art, Zweck & Umfang der Verarbeitung" testId="sb-avv-processing">
          <p><strong>Verarbeitungsvorgänge:</strong> Speichern, Anzeigen, Prüfen, Korrigieren
          (im Rahmen erteilter Vollmachten), Kategorisieren, Exportieren (DATEV/BMD/CSV/XML),
          Übermitteln (Kommunikation Kanzlei ↔ Mandant), Löschen.</p>
          <p><strong>Zweck:</strong> Unterstützung der Kanzlei bei der buchhalterischen und
          steuerlichen Betreuung ihrer Mandanten.</p>
        </SectionCard>

        <SectionCard icon={Users} title="§ 3 Betroffene Personen & Datenkategorien" testId="sb-avv-data">
          <p><strong>Kategorien betroffener Personen:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Mandanten der Kanzlei (ivoliq-Nutzer)</li>
            <li>Geschäftspartner der Mandanten (Kunden, Lieferanten — soweit auf Belegen/Rechnungen enthalten)</li>
            <li>Mitarbeiter der Kanzlei (Zugangsdaten, Profilinformationen)</li>
          </ul>
          <p><strong>Datenkategorien:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Stammdaten (Name, Anschrift, Steuernummern, UID)</li>
            <li>Rechnungs- und Belegdaten (Beträge, Positionen, Bankverbindungen)</li>
            <li>Steuerliche Auswertungen (UVA/UStVA, EÜR-Daten, AfA)</li>
            <li>Kommunikationsinhalte (Nachrichten, Anmerkungen, Rückfragen)</li>
          </ul>
        </SectionCard>

        <SectionCard icon={Lock} title="§ 4 Pflichten von ivoliq" testId="sb-avv-duties">
          <ul className="list-disc pl-5 space-y-2">
            <li>Verarbeitung ausschließlich auf dokumentierte Weisung der Kanzlei (Weisungen
            erfolgen regelmäßig über die Funktionen des SB-Portals).</li>
            <li>Verpflichtung aller mit der Verarbeitung befassten Personen zur Vertraulichkeit.</li>
            <li>Umsetzung technischer und organisatorischer Maßnahmen (TOM) gemäß Art. 32 DSGVO —
            es gelten die in{' '}
            <Link to="/avv" className="text-emerald-600 hover:underline">Anhang 1 des Nutzer-AVV</Link>{' '}
            beschriebenen Maßnahmen (Verschlüsselung, Zugriffskontrolle, ISO-27001-Rechenzentren,
            Backups) entsprechend.</li>
            <li>Unterstützung der Kanzlei bei der Erfüllung ihrer Pflichten aus Art. 32–36 DSGVO
            (Sicherheit, Meldepflichten, Datenschutz-Folgenabschätzung).</li>
            <li>Hosting und Speicherung ausschließlich in Rechenzentren innerhalb der EU
            (Hetzner Online GmbH, Deutschland).</li>
          </ul>
        </SectionCard>

        <SectionCard icon={Server} title="§ 5 Unterauftragsverarbeiter" testId="sb-avv-subprocessors">
          <p>
            Die Kanzlei genehmigt die unten aufgeführten Unterauftragsverarbeiter. Beabsichtigte
            Änderungen werden 30 Tage im Voraus per E-Mail mitgeteilt; die Kanzlei kann aus
            wichtigem datenschutzrechtlichem Grund widersprechen.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-2 border">Dienstleister</th>
                  <th className="p-2 border">Zweck</th>
                  <th className="p-2 border">Standort</th>
                  <th className="p-2 border">Garantien</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border">Hetzner Online GmbH</td>
                  <td className="p-2 border">Hosting, Object Storage, Datenbank</td>
                  <td className="p-2 border">DE (Falkenstein / Nürnberg)</td>
                  <td className="p-2 border">ISO 27001, AVV</td>
                </tr>
                <tr>
                  <td className="p-2 border">Sendinblue SAS (Brevo)</td>
                  <td className="p-2 border">Transaktionale E-Mails (Einladungen, Rechnungen, Benachrichtigungen)</td>
                  <td className="p-2 border">FR / EU</td>
                  <td className="p-2 border">DPA</td>
                </tr>
                <tr>
                  <td className="p-2 border">OpenAI, Inc. / Anthropic, PBC</td>
                  <td className="p-2 border">KI-Belegerkennung / OCR (nur soweit vom Mandanten genutzt)</td>
                  <td className="p-2 border">US</td>
                  <td className="p-2 border">SCC, EU-US DPF</td>
                </tr>
                <tr>
                  <td className="p-2 border">Emergent Labs, Inc.</td>
                  <td className="p-2 border">Fallback Object Storage, LLM für OCR</td>
                  <td className="p-2 border">US / EU</td>
                  <td className="p-2 border">SCC, Opt-in</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>

        <SectionCard icon={ClipboardCheck} title="§ 6 Betroffenenrechte & Unterstützung" testId="sb-avv-rights">
          <p>
            ivoliq unterstützt die Kanzlei mit geeigneten technischen und organisatorischen
            Maßnahmen bei der Beantwortung von Anträgen betroffener Personen (Auskunft,
            Berichtigung, Löschung, Datenübertragbarkeit). Anträge, die direkt bei ivoliq
            eingehen, werden unverzüglich an die Kanzlei weitergeleitet.
          </p>
        </SectionCard>

        <SectionCard icon={AlertCircle} title="§ 7 Meldepflichten bei Datenpannen" testId="sb-avv-breach">
          <p>
            ivoliq meldet der Kanzlei Verletzungen des Schutzes personenbezogener Daten
            unverzüglich, spätestens innerhalb von 48 Stunden nach Bekanntwerden, mit allen
            für die Meldung nach Art. 33 DSGVO erforderlichen Informationen.
          </p>
        </SectionCard>

        <SectionCard icon={Trash2} title="§ 8 Löschung & Rückgabe" testId="sb-avv-deletion">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Nach Beendigung des SB-Vertrags werden die kanzleibezogenen Daten (Kanzlei-Profil,
              Prüfvermerke, Kommunikationsverläufe der Kanzlei) nach Wahl der Kanzlei gelöscht
              oder zurückgegeben, soweit keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
            </li>
            <li>
              <strong>Wichtig:</strong> Die Accounts und Daten der Mandanten bleiben von der
              Beendigung unberührt — sie unterliegen den eigenständigen Nutzungsverträgen der
              Mandanten mit ivoliq.
            </li>
          </ul>
        </SectionCard>

        <SectionCard icon={ShieldCheck} title="§ 9 Nachweise & Kontrollrechte" testId="sb-avv-audit">
          <p>
            ivoliq stellt der Kanzlei alle erforderlichen Informationen zum Nachweis der
            Einhaltung dieses Vertrags zur Verfügung (TOM-Beschreibung, Liste der
            Unterauftragsverarbeiter, Zertifikate der Rechenzentren). Vor-Ort-Kontrollen
            sind nach Ankündigung mit angemessener Frist während üblicher Geschäftszeiten
            möglich, soweit Auskünfte und Nachweise nicht ausreichen.
          </p>
        </SectionCard>

        <SectionCard icon={Scale} title="§ 10 Schlussbestimmungen" testId="sb-avv-final">
          <ul className="list-disc pl-5 space-y-2">
            <li>Es gilt österreichisches Recht; Gerichtsstand ist Wien.</li>
            <li>Bei Widersprüchen zwischen diesem AVV und dem SB-Vertrag geht in
            datenschutzrechtlichen Fragen dieser AVV vor.</li>
            <li>Salvatorische Klausel: Die Unwirksamkeit einzelner Bestimmungen berührt die
            Wirksamkeit der übrigen nicht.</li>
          </ul>
        </SectionCard>
      </div>

      <p className="mt-8 text-xs text-gray-400">
        ivoliq GmbH · Wien, Österreich · Datenschutzfragen: admin@ivoliq.io ·{' '}
        <Link to="/steuerberater-agb" className="hover:underline">SB-Vertrag</Link> ·{' '}
        <Link to="/privacy" className="hover:underline">Datenschutzerklärung</Link>
      </p>
    </div>
  );
}
